import { useCallback, useEffect, useRef, useState } from "react";
import { portalGetTicketMessages } from "../../../api/clientPortal";
import {
  PORTAL_MESSAGES_PAGE_SIZE,
  mergePortalMessages,
  maxPortalMessageId,
} from "../utils/portalMessagesState";

export function usePortalTicketMessages(ticketId) {
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextBefore, setNextBefore] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadingRef = useRef(false);
  const syncInFlightRef = useRef(false);
  const messagesRef = useRef([]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const reset = useCallback(() => {
    setMessages([]);
    setHasMore(false);
    setNextBefore(null);
  }, []);

  const loadMessages = useCallback(
    async ({ before, append = false } = {}) => {
      if (!ticketId || loadingRef.current) return;
      loadingRef.current = true;
      if (append) setIsLoadingMore(true);
      else setIsLoading(true);

      try {
        const result = await portalGetTicketMessages(ticketId, {
          before,
          limit: PORTAL_MESSAGES_PAGE_SIZE,
        });
        const incoming = result.messages || [];

        if (append) {
          setMessages((prev) => mergePortalMessages(prev, incoming, "prepend"));
        } else {
          setMessages(mergePortalMessages([], incoming, "replace"));
        }

        setHasMore(Boolean(result.hasMore));
        setNextBefore(result.nextBefore);
      } catch {
        if (!append) {
          setMessages([]);
          setHasMore(false);
          setNextBefore(null);
        }
      } finally {
        loadingRef.current = false;
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [ticketId]
  );

  const loadMore = useCallback(() => {
    if (!hasMore || !nextBefore || loadingRef.current) return;
    loadMessages({ before: nextBefore, append: true });
  }, [hasMore, nextBefore, loadMessages]);

  const syncLatest = useCallback(async () => {
    if (!ticketId || syncInFlightRef.current) return;
    syncInFlightRef.current = true;
    try {
      const after = maxPortalMessageId(messagesRef.current);
      if (after) {
        const result = await portalGetTicketMessages(ticketId, {
          after,
          limit: PORTAL_MESSAGES_PAGE_SIZE,
        });
        if (result.messages?.length) {
          setMessages((prev) => mergePortalMessages(prev, result.messages, "merge"));
        }
        return;
      }
      if (!loadingRef.current) {
        await loadMessages();
      }
    } catch {
      /* ignore background sync */
    } finally {
      syncInFlightRef.current = false;
    }
  }, [ticketId, loadMessages]);

  useEffect(() => {
    reset();
    if (ticketId) {
      loadMessages();
    }
  }, [ticketId, loadMessages, reset]);

  useEffect(() => {
    if (!ticketId || isLoading) return undefined;
    const timer = window.setTimeout(() => syncLatest(), 1500);
    return () => window.clearTimeout(timer);
  }, [ticketId, isLoading, syncLatest]);

  useEffect(() => {
    if (!ticketId || typeof document === "undefined") return undefined;

    const POLL_INTERVAL_MS = 10_000;

    const runSync = () => {
      if (document.visibilityState !== "visible") return;
      syncLatest();
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") runSync();
    };

    window.addEventListener("focus", runSync);
    document.addEventListener("visibilitychange", handleVisibility);

    const timer = setInterval(runSync, POLL_INTERVAL_MS);

    return () => {
      window.removeEventListener("focus", runSync);
      document.removeEventListener("visibilitychange", handleVisibility);
      clearInterval(timer);
    };
  }, [ticketId, syncLatest]);

  return {
    messages,
    setMessages,
    hasMore,
    isLoading,
    isLoadingMore,
    loadMessages,
    loadMore,
    syncLatest,
    reset,
  };
}
